'use client';

import React, { useMemo, useState, useCallback } from 'react';
import {
  FileText,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateRangePicker, getParisCurrentWeek, formatParisDate } from '@/components/ui/date-range-picker';
import { trpc } from '@/lib/api/trpc';
import { formatMoney } from '@/lib/money';
import { usePartnerAdmin } from '@/hooks/use-partner-admin';
import { useUrlDateRange } from '@/hooks/use-url-date-range';
import { useParams } from 'react-router-dom';

function MetricCard({
  title,
  value,
  subtitle,
  icon: Icon,
  isLoading,
  className,
}: {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ComponentType<{ className?: string }>;
  isLoading?: boolean;
  className?: string;
}) {
  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="text-muted-foreground h-4 w-4" />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-7 w-32" />
        ) : (
          <>
            <div className="text-2xl font-bold">{value}</div>
            {subtitle && (
              <p className="text-muted-foreground text-xs">{subtitle}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

type StatusFilter = 'all' | 'open' | 'failed';
type SortField = 'totalInvoiced' | 'totalPaid' | 'totalOpen' | 'totalFailed' | 'reportCount' | 'orgName';
type SortDir = 'asc' | 'desc';

interface AccountRow {
  accountId: string;
  orgName: string | null;
  orgSlug: string | null;
  partner: string | null;
  totalInvoiced: number;
  totalPaid: number;
  totalOpen: number;
  totalFailed: number;
  reportCount: number;
}

const PAGE_SIZE = 25;

function SortIcon({ field, sortField, sortDir }: { field: SortField; sortField: SortField; sortDir: SortDir }) {
  if (field !== sortField) return <ArrowUpDown className="ml-1 inline h-3 w-3 opacity-40" />;
  return sortDir === 'asc'
    ? <ArrowUp className="ml-1 inline h-3 w-3" />
    : <ArrowDown className="ml-1 inline h-3 w-3" />;
}

function InvoicedAccountsTable({
  accounts,
  isLoading,
}: {
  accounts: AccountRow[];
  isLoading: boolean;
}) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortField, setSortField] = useState<SortField>('totalInvoiced');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(0);

  const toggleSort = useCallback((field: SortField) => {
    setSortField(prev => {
      if (prev === field) {
        setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        return field;
      }
      setSortDir('desc');
      return field;
    });
    setPage(0);
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let rows = accounts;
    if (statusFilter === 'open') {
      rows = rows.filter(a => a.totalOpen > 0);
    } else if (statusFilter === 'failed') {
      rows = rows.filter(a => a.totalFailed > 0);
    }
    if (q) {
      rows = rows.filter(a =>
        (a.orgName ?? '').toLowerCase().includes(q) ||
        (a.orgSlug ?? '').toLowerCase().includes(q) ||
        a.accountId.toLowerCase().includes(q)
      );
    }
    rows = [...rows].sort((a, b) => {
      if (sortField === 'orgName') {
        const cmp = (a.orgName ?? '').localeCompare(b.orgName ?? '');
        return sortDir === 'asc' ? cmp : -cmp;
      }
      const av = (a as any)[sortField] ?? 0;
      const bv = (b as any)[sortField] ?? 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return rows;
  }, [accounts, search, statusFilter, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const totals = useMemo(() => {
    return filtered.reduce(
      (acc, a) => ({
        invoiced: acc.invoiced + a.totalInvoiced,
        paid: acc.paid + a.totalPaid,
        open: acc.open + a.totalOpen,
        failed: acc.failed + a.totalFailed,
        reports: acc.reports + a.reportCount,
      }),
      { invoiced: 0, paid: 0, open: 0, failed: 0, reports: 0 },
    );
  }, [filtered]);

  const thClass = 'cursor-pointer select-none hover:bg-muted/50 transition-colors';

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-lg">Accounts by Invoiced Fees</CardTitle>
            <CardDescription>
              Actual billing reports sent via Stripe invoices
            </CardDescription>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1">
              {([['all', 'All'], ['open', 'Open'], ['failed', 'Failed']] as const).map(([value, label]) => (
                <Button
                  key={value}
                  variant={statusFilter === value ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => { setStatusFilter(value); setPage(0); }}
                >
                  {label}
                </Button>
              ))}
            </div>
            <div className="relative">
              <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder="Search accounts..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                className="w-[240px] pl-9"
              />
            </div>
            <span className="text-muted-foreground text-xs whitespace-nowrap">
              {filtered.length} / {accounts.length} accounts
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : accounts.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center text-sm">No billing reports found for this period.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">#</TableHead>
                    <TableHead className={thClass} onClick={() => toggleSort('orgName')}>
                      Account <SortIcon field="orgName" sortField={sortField} sortDir={sortDir} />
                    </TableHead>
                    <TableHead className={`text-right ${thClass}`} onClick={() => toggleSort('totalInvoiced')}>
                      Total Invoiced <SortIcon field="totalInvoiced" sortField={sortField} sortDir={sortDir} />
                    </TableHead>
                    <TableHead className={`text-right ${thClass}`} onClick={() => toggleSort('totalPaid')}>
                      Paid <SortIcon field="totalPaid" sortField={sortField} sortDir={sortDir} />
                    </TableHead>
                    <TableHead className={`text-right ${thClass}`} onClick={() => toggleSort('totalOpen')}>
                      Open <SortIcon field="totalOpen" sortField={sortField} sortDir={sortDir} />
                    </TableHead>
                    <TableHead className={`text-right ${thClass}`} onClick={() => toggleSort('totalFailed')}>
                      Failed <SortIcon field="totalFailed" sortField={sortField} sortDir={sortDir} />
                    </TableHead>
                    <TableHead className={`text-right ${thClass}`} onClick={() => toggleSort('reportCount')}>
                      Invoices <SortIcon field="reportCount" sortField={sortField} sortDir={sortDir} />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageRows.map((account, idx) => (
                    <TableRow key={account.accountId}>
                      <TableCell className="text-muted-foreground font-mono text-xs">
                        {page * PAGE_SIZE + idx + 1}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">
                            {account.orgName ?? account.accountId.slice(0, 15)}
                          </span>
                          <span className="text-muted-foreground font-mono text-xs">
                            {account.orgSlug ?? account.accountId.slice(0, 18)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">
                        {formatMoney(account.totalInvoiced, 'USD')}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-emerald-600">
                        {formatMoney(account.totalPaid, 'USD')}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-amber-600">
                        {account.totalOpen > 0 ? formatMoney(account.totalOpen, 'USD') : '-'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-red-600">
                        {account.totalFailed > 0 ? formatMoney(account.totalFailed, 'USD') : '-'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {account.reportCount}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-muted/50 font-medium">
                    <TableCell />
                    <TableCell className="text-sm font-semibold">
                      Total ({filtered.length} accounts)
                    </TableCell>
                    <TableCell className="text-right font-bold tabular-nums">
                      {formatMoney(totals.invoiced, 'USD')}
                    </TableCell>
                    <TableCell className="text-right font-bold tabular-nums text-emerald-600">
                      {formatMoney(totals.paid, 'USD')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-amber-600">
                      {totals.open > 0 ? formatMoney(totals.open, 'USD') : '-'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-red-600">
                      {totals.failed > 0 ? formatMoney(totals.failed, 'USD') : '-'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {totals.reports}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <span className="text-muted-foreground text-sm">
                  Page {page + 1} of {totalPages}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="mr-1 h-4 w-4" /> Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    Next <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function PartnerAdminInvoicedPage() {
  const { partner: partnerParam } = useParams<{ partner: string }>();
  const { partner, canView } = usePartnerAdmin(partnerParam);
  const [dateRange, setDateRange] = useUrlDateRange(getParisCurrentWeek);
  const periodStart = dateRange.from;
  const periodEnd = dateRange.to;

  const { data, isLoading, error, refetch } = trpc.admin.partnerBillingReportsSummary.useQuery(
    { partner: partner ?? undefined, periodStart, periodEnd },
    { staleTime: 2 * 60 * 1000, refetchOnWindowFocus: false, enabled: canView && !!partner },
  );

  if (!canView) return null;

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="py-8 text-center">
          <p className="text-destructive font-medium">Failed to load invoiced data</p>
          <p className="text-muted-foreground mt-1 text-sm">{error.message}</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={() => refetch()}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const g = data?.globalTotals;

  return (
    <div className="space-y-6">
      {/* Date Picker */}
      <div className="flex items-center gap-4">
        <DateRangePicker value={dateRange} onChange={setDateRange} />
        <span className="text-muted-foreground text-xs">
          {periodStart ? formatParisDate(periodStart, { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
          {' — '}
          {periodEnd ? formatParisDate(periodEnd, { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
          {' '}(Paris)
        </span>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Invoiced"
          value={g ? formatMoney(g.totalInvoiced, 'USD') : '-'}
          subtitle={g ? `${g.totalReports} invoices across ${g.accountCount} accounts` : ''}
          icon={FileText}
          isLoading={isLoading}
        />
        <MetricCard
          title="Total Paid"
          value={g ? formatMoney(g.totalPaid, 'USD') : '-'}
          subtitle={g ? `${g.collectionRate}% collection rate` : ''}
          icon={CheckCircle2}
          isLoading={isLoading}
          className="border-emerald-200 dark:border-emerald-900"
        />
        <MetricCard
          title="Open / Draft"
          value={g ? formatMoney(g.totalOpen, 'USD') : '-'}
          subtitle="Awaiting payment"
          icon={Clock}
          isLoading={isLoading}
          className="border-amber-200 dark:border-amber-900"
        />
        <MetricCard
          title="Failed / Uncollectible"
          value={g ? formatMoney(g.totalFailed, 'USD') : '-'}
          subtitle={g && g.totalVoid > 0 ? `+ ${formatMoney(g.totalVoid, 'USD')} voided` : 'Payment failed'}
          icon={AlertTriangle}
          isLoading={isLoading}
          className="border-red-200 dark:border-red-900"
        />
      </div>

      {/* Accounts Table */}
      <InvoicedAccountsTable
        accounts={data?.accountRows ?? []}
        isLoading={isLoading}
      />
    </div>
  );
}
